import {Button, Icon, SearchField, Table} from '@wandb/components';
import React from 'react';

import {
  ArtifactMetricValuesDocument,
  type ArtifactMetricValuesQuery,
} from '../../generated/graphql';
import {logError} from '../../services/errors/errorReporting';
import {useApolloClient} from '../../state/hooks';
import {propagateErrorsContext} from '../../util/errors';
import {type ArtifactVersionsTableData} from '../ArtifactOverview/hooks';
import {
  type ArtifactVersionSelectionMetricValues,
  type ArtifactVersionSelectionTableRow,
  getArtifactVersionSelectionColumns,
} from './ArtifactVersionSelectionTableColumns';
import {
  ARTIFACT_VERSION_SELECTION_LIMIT,
  ARTIFACT_VERSION_SELECTION_PAGE_SIZE,
  toggleMembershipSelection,
  togglePageMembershipSelection,
} from './versionSelectionUtils';

type ArtifactVersionSelectionTableProps = {
  artifactCollectionId?: string;
  hasConfiguredPanels: boolean;
  loading: boolean;
  membershipIds: string[];
  metricNames: string[];
  page: number;
  readOnly?: boolean;
  rowCount: number;
  rows: ArtifactVersionsTableData[];
  search: string;
  emptyRowMessage?: string;
  searchStatus?: string;
  setPage: (page: number) => void;
  setSearch: (search: string) => void;
  updateMembershipIds: (membershipIds: string[]) => void;
};

export const ArtifactVersionSelectionTable: React.FC<
  ArtifactVersionSelectionTableProps
> = ({
  artifactCollectionId,
  hasConfiguredPanels,
  loading,
  membershipIds,
  metricNames,
  page,
  readOnly,
  rowCount,
  rows,
  search,
  emptyRowMessage = 'No artifact versions found.',
  searchStatus,
  setPage,
  setSearch,
  updateMembershipIds,
}) => {
  const metricValues = useArtifactVersionSelectionMetricValues({
    membershipIds,
    metricNames,
    artifactCollectionId,
  });
  const selectionLimitReached =
    membershipIds.length >= ARTIFACT_VERSION_SELECTION_LIMIT;
  const tableRows: ArtifactVersionSelectionTableRow[] = rows.map(row => ({
    ...row,
    aliases: row.aliases.map(alias => ({alias})),
    selected: membershipIds.includes(row.membershipId),
  }));
  const pageMembershipIds = rows.map(row => row.membershipId);
  const allPageRowsSelected =
    pageMembershipIds.length > 0 &&
    pageMembershipIds.every(membershipId =>
      membershipIds.includes(membershipId)
    );
  const pageSelectionDisabled =
    pageMembershipIds.length === 0 ||
    (!allPageRowsSelected && selectionLimitReached);
  const toggleSelection = (membershipId: string) => {
    updateMembershipIds(toggleMembershipSelection(membershipIds, membershipId));
  };
  const togglePageSelection = () => {
    updateMembershipIds(
      togglePageMembershipSelection(membershipIds, pageMembershipIds)
    );
  };
  const clearSelection = () => {
    updateMembershipIds([]);
  };
  const handleSearchChange = (nextSearch: string) => {
    setSearch(nextSearch);
    setPage(0);
  };
  const pagination =
    rowCount > ARTIFACT_VERSION_SELECTION_PAGE_SIZE
      ? {
          hasPreviousPage: page > 0,
          hasNextPage:
            (page + 1) * ARTIFACT_VERSION_SELECTION_PAGE_SIZE < rowCount,
          onPrevious: () => setPage(Math.max(0, page - 1)),
          onNext: () => setPage(page + 1),
          range: {
            pageIndex: page,
            pageSize: ARTIFACT_VERSION_SELECTION_PAGE_SIZE,
            totalCount: rowCount,
          },
        }
      : undefined;
  const renderEmptyState = () => (
    <div className="py-24 text-center text-sm text-wb-text-tertiary dark:text-moon-400">
      {emptyRowMessage}
    </div>
  );
  const columns = getArtifactVersionSelectionColumns({
    allPageRowsSelected,
    metricValues,
    metricNames,
    pageSelectionDisabled,
    selectionLimitReached,
    togglePageSelection,
    toggleSelection,
  });
  const renderToolbar = () => (
    <div className="flex w-full flex-wrap items-center gap-8">
      <div className="flex min-w-[220px] max-w-[360px] flex-1 items-center gap-8">
        <div className="min-w-[220px] max-w-[360px] flex-1">
          <SearchField
            aria-label="Search artifact version aliases and tags"
            data-test="artifact-version-selection-search"
            isDisabled={readOnly}
            fullWidth
            onChange={handleSearchChange}
            placeholder="Search aliases/tags"
            value={search}
          />
        </div>
      </div>
      <div className="ml-auto flex flex-wrap items-center justify-end gap-8 text-base text-wb-text-secondary dark:text-moon-400">
        {hasConfiguredPanels ? (
          <span>
            Plotted versions{' '}
            <span className="font-semibold">{membershipIds.length}</span> of{' '}
            {rowCount}
          </span>
        ) : (
          <span className="inline-flex items-center gap-4">
            <Icon icon="warning" />
            <span>
              <span className="font-semibold">Hidden on publish.</span> No
              panels added
            </span>
          </span>
        )}
        {hasConfiguredPanels && membershipIds.length > 0 && !readOnly && (
          <Button size="sm" variant="ghost" onPress={clearSelection}>
            Clear all
          </Button>
        )}
        {selectionLimitReached && !readOnly && (
          <span>Selection limit is {ARTIFACT_VERSION_SELECTION_LIMIT}.</span>
        )}
        {searchStatus && <span role="status">{searchStatus}</span>}
      </div>
    </div>
  );

  return (
    <div className="[&_nav[aria-label='Artifact_versions_pagination']]:!static">
      <Table
        aria-label="Artifact versions"
        isLoading={loading}
        columnResizable
        pagination={pagination}
        scrollRoot={null}
        toolbar={renderToolbar}>
        <Table.Header columns={columns}>
          {({name, Cell: _Cell, ...columnProps}) => (
            <Table.Column {...columnProps}>{name}</Table.Column>
          )}
        </Table.Header>
        <Table.Body items={tableRows} renderEmptyState={renderEmptyState}>
          {(row: ArtifactVersionSelectionTableRow) => (
            <Table.Row id={row.membershipId} columns={columns}>
              {({Cell}) => <Table.Cell>{Cell({row})}</Table.Cell>}
            </Table.Row>
          )}
        </Table.Body>
      </Table>
    </div>
  );
};

function useArtifactVersionSelectionMetricValues({
  artifactCollectionId,
  membershipIds,
  metricNames,
}: {
  artifactCollectionId?: string;
  membershipIds: string[];
  metricNames: string[];
}): ArtifactVersionSelectionMetricValues {
  const client = useApolloClient();
  const uniqueMetricNames = React.useMemo(
    () =>
      Array.from(new Set(metricNames.filter(metricName => metricName !== ''))),
    [metricNames]
  );
  const variablesByMetricName = React.useMemo(
    () =>
      uniqueMetricNames.map(metricName => ({
        artifactCollectionId: artifactCollectionId ?? '',
        membershipIds,
        metricName,
      })),
    [artifactCollectionId, membershipIds, uniqueMetricNames]
  );
  const [metricValues, setMetricValues] =
    React.useState<ArtifactVersionSelectionMetricValues>({});

  React.useEffect(() => {
    if (
      artifactCollectionId == null ||
      membershipIds.length === 0 ||
      variablesByMetricName.length === 0
    ) {
      setMetricValues({});
      return;
    }

    let cancelled = false;

    const readRowsByMetricName = (data?: ArtifactMetricValuesQuery | null) => {
      const artifactCollection = data?.artifactCollection;
      return artifactCollection?.__typename === 'ArtifactPortfolio'
        ? artifactCollection.artifactMetricValues
        : [];
    };

    const getMetricValuesFromData = (
      currentMetricName: string,
      data?: ArtifactMetricValuesQuery | null
    ) => {
      const valuesByMembershipId: Record<string, number | null> = {};
      for (const row of readRowsByMetricName(data)) {
        valuesByMembershipId[row.membershipId] =
          typeof row.value === 'number' && Number.isFinite(row.value)
            ? row.value
            : null;
      }

      return [currentMetricName, valuesByMembershipId] as const;
    };

    // Panels fetch these same metric values for the charts. Read synchronously
    // from Apollo first so the table can reuse cached chart data, then issue a
    // cache-first query to fill misses without changing the variable shape.
    const cachedEntries = variablesByMetricName.map(variables => {
      let cachedData: ArtifactMetricValuesQuery | null = null;
      try {
        cachedData = client.readQuery<ArtifactMetricValuesQuery>({
          query: ArtifactMetricValuesDocument,
          variables,
        });
      } catch {
        cachedData = null;
      }
      return getMetricValuesFromData(variables.metricName, cachedData);
    });

    setMetricValues(Object.fromEntries(cachedEntries));

    void Promise.all(
      variablesByMetricName.map(async variables => {
        const {data} = await client.query<ArtifactMetricValuesQuery>({
          query: ArtifactMetricValuesDocument,
          variables,
          fetchPolicy: 'cache-first',
          context: propagateErrorsContext(),
        });
        return getMetricValuesFromData(variables.metricName, data);
      })
    )
      .then(entries => {
        if (!cancelled) {
          setMetricValues(Object.fromEntries(entries));
        }
      })
      .catch(error => {
        logError(error);
      });

    return () => {
      cancelled = true;
    };
  }, [artifactCollectionId, client, membershipIds, variablesByMetricName]);

  return metricValues;
}
